import React from 'react';
import { Snowflake, Flame, Check, CheckCircle2, Plus } from 'lucide-react';
import { cropImage } from '@/lib/cropImage';

const ACTION = {
  sow_direct: { label: 'Sow Direct', bg: 'rgba(107,159,74,0.16)', fg: '#4E7A33' },
  transplant: { label: 'Transplant', bg: 'rgba(143,180,214,0.18)', fg: '#3E6E96' },
  harvest: { label: 'Harvest', bg: 'rgba(201,168,91,0.16)', fg: '#8A6D1F' },
};
const DIFF = {
  beginner: { label: 'Beginner', c: '#4E7A33' },
  intermediate: { label: 'Intermediate', c: '#C9A85B' },
  advanced: { label: 'Advanced', c: '#C66B4A' },
};
const NEXT = { none: 'planned', planned: 'planted', planted: null };

// Frosted-glass crop card with a real vegetable photo. Checkbox cycles
// unmarked -> Planned (amber) -> Planted (green). Marginal crops dim + warn.
export function SeasonCropCard({ crop, onCycle, onAddToPlanner, onOpenPlanner }) {
  const status = crop.status || null;
  const marginal = !!crop.marginal;
  const diff = DIFF[(crop.difficulty || 'beginner').toLowerCase()] || DIFF.beginner;
  const cycle = () => onCycle && onCycle(NEXT[status || 'none']);

  return (
    <div data-testid={`season-crop-${crop.crop_id}`} className="hg-glass hg-float rounded-2xl p-4 flex items-center gap-3">
      <div className={`flex items-center gap-3 flex-1 min-w-0 ${marginal ? 'opacity-50' : ''}`}>
        <img src={cropImage(crop.name, crop.category)} alt={crop.name} loading="lazy"
          className="w-14 h-14 rounded-xl object-cover flex-shrink-0" style={{ background: '#D9E1CB' }} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-bold text-[#1A4331] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{crop.name}</p>
            {marginal && (
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0"
                style={{ background: crop.warning === 'heat' ? 'rgba(198,107,74,0.14)' : 'rgba(143,180,214,0.18)' }}
                title={crop.warning === 'heat' ? 'Heat-marginal this month' : 'Frost-marginal this month'}
              >
                {crop.warning === 'heat'
                  ? <Flame className="w-3 h-3" style={{ color: '#C66B4A' }} />
                  : <Snowflake className="w-3 h-3" style={{ color: '#3E6E96' }} />}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {crop.actions.map((a) => {
              const c = ACTION[a];
              if (!c) return null;
              return (
                <span key={a} className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: c.bg, color: c.fg }}>
                  {c.label}
                </span>
              );
            })}
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(26,67,49,0.06)', color: '#5C665C' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: diff.c }} />
              {diff.label}
            </span>
            {crop.in_planner ? (
              <button onClick={(e) => { e.stopPropagation(); onOpenPlanner && onOpenPlanner(); }} data-testid={`season-inplanner-${crop.crop_id}`}
                className="hg-press inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(107,159,74,0.16)', color: '#4E7A33' }}>
                <CheckCircle2 className="w-3 h-3" /> In planner
              </button>
            ) : (
              <button onClick={(e) => { e.stopPropagation(); onAddToPlanner && onAddToPlanner(); }} data-testid={`season-addplanner-${crop.crop_id}`}
                className="hg-press inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(26,67,49,0.06)', color: '#1A4331' }}>
                <Plus className="w-3 h-3" /> Add to planner
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <Checkbox status={status} onClick={cycle} cropId={crop.crop_id} />
        {status && (
          <span className="text-[10px] font-semibold" style={{ color: status === 'planted' ? '#4E7A33' : '#8A6D1F' }}>
            {status === 'planted' ? 'Planted' : 'Planned'}
          </span>
        )}
      </div>
    </div>
  );
}

function Checkbox({ status, onClick, cropId }) {
  const base = 'hg-press flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center';
  if (status === 'planted') {
    return (
      <button data-testid={`season-check-${cropId}`} onClick={onClick} className={base} style={{ background: '#6B9F4A' }} aria-label="Planted">
        <span className="flex -space-x-2">
          <Check className="w-4 h-4 text-[#FAF6EC]" />
          <Check className="w-4 h-4 text-[#FAF6EC]" />
        </span>
      </button>
    );
  }
  if (status === 'planned') {
    return (
      <button data-testid={`season-check-${cropId}`} onClick={onClick} className={base} style={{ background: '#C9A85B' }} aria-label="Planned">
        <Check className="w-4 h-4 text-[#FAF6EC]" />
      </button>
    );
  }
  return (
    <button data-testid={`season-check-${cropId}`} onClick={onClick} className={`${base} border-2`} style={{ borderColor: 'rgba(26,67,49,0.22)', background: 'rgba(255,255,255,0.5)' }} aria-label="Unmarked" />
  );
}

export default SeasonCropCard;
